/* eslint-disable indent */
import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import Image from "next/image";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { IArtStyle } from "types";
import { Label } from "utils/styling/typography.styled";
import { ArtStyleCard, ArtStyleName, CloseButton, DefaultModelContainer, HeaderContainer, LabelWrapper, ModelName, ModelsGrid, NewBadge, SearchContainer, SearchIcon, SearchInputContainer, StyledDialogContent, StyledDialogHeader, StyledDialogTitle, StyledInput, StyledScrollArea } from "./ArtStyleSelection.styled";
interface ArtStyleSelectionProps {
  artStyles: IArtStyle[];
  onStyleChange: (field: string, value: string) => void;
  isDisabled?: boolean;
}
const ArtStyleSelection: React.FC<ArtStyleSelectionProps> = ({
  artStyles,
  onStyleChange,
  isDisabled
}) => {
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const filteredArtStyles = artStyles.filter(style => style.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const isInitialStyleSet = useRef<boolean>(false);
  const [selectedArtStyle, setSelectedArtStyle] = useState<IArtStyle | null>();
  const searchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (modalIsOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [modalIsOpen]);
  useEffect(() => {
    if (artStyles.length > 0 && !isInitialStyleSet.current && !selectedArtStyle) {
      const defaultStyle = artStyles.filter(style => style.name === "Realistic")[0];
      setSelectedArtStyle(defaultStyle);
      onStyleChange("Art Style", defaultStyle.name);
      isInitialStyleSet.current = true;
    }
  }, [artStyles, onStyleChange, selectedArtStyle, setSelectedArtStyle]);
  const handleArtStyleClick = (style: IArtStyle) => {
    setSelectedArtStyle(style);
    onStyleChange("Art Style", style.name);
    setModalIsOpen(false);
  };
  const openModal = () => setModalIsOpen(true);
  const handleClick = () => {
    if (!isDisabled) {
      openModal();
    }
  };
  return <Dialog open={modalIsOpen} onOpenChange={setModalIsOpen} data-sentry-element="Dialog" data-sentry-component="ArtStyleSelection" data-sentry-source-file="index.tsx">
            <div>
                <LabelWrapper data-sentry-element="LabelWrapper" data-sentry-source-file="index.tsx">
                    <Label data-sentry-element="Label" data-sentry-source-file="index.tsx">Select a Model</Label>
                </LabelWrapper>
                {selectedArtStyle && <DialogTrigger asChild onClick={handleClick} disabled={isDisabled}>
                        <DefaultModelContainer>
                            <Image src={selectedArtStyle.url} alt={selectedArtStyle.name} width={50} height={50} priority />
                            <div>
                                <ModelName>{selectedArtStyle.name}</ModelName>
                            </div>
                        </DefaultModelContainer>
                    </DialogTrigger>}
                <StyledDialogContent data-sentry-element="StyledDialogContent" data-sentry-source-file="index.tsx">
                    <StyledDialogHeader data-sentry-element="StyledDialogHeader" data-sentry-source-file="index.tsx">
                        <HeaderContainer data-sentry-element="HeaderContainer" data-sentry-source-file="index.tsx">
                            <StyledDialogTitle data-sentry-element="StyledDialogTitle" data-sentry-source-file="index.tsx">
                                Select an Art Style
                            </StyledDialogTitle>
                            <CloseButton variant="ghost" onClick={() => setModalIsOpen(false)} data-sentry-element="CloseButton" data-sentry-source-file="index.tsx">
                                <X className="h-4 w-4" data-sentry-element="X" data-sentry-source-file="index.tsx" />
                                <span className="sr-only">Close</span>
                            </CloseButton>
                        </HeaderContainer>
                    </StyledDialogHeader>
                    <SearchContainer data-sentry-element="SearchContainer" data-sentry-source-file="index.tsx">
                        <SearchInputContainer data-sentry-element="SearchInputContainer" data-sentry-source-file="index.tsx">
                            <SearchIcon data-sentry-element="SearchIcon" data-sentry-source-file="index.tsx" />
                            <StyledInput placeholder="Search art styles..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-sentry-element="StyledInput" data-sentry-source-file="index.tsx" />
                        </SearchInputContainer>
                    </SearchContainer>
                    <StyledScrollArea data-sentry-element="StyledScrollArea" data-sentry-source-file="index.tsx">
                        <ModelsGrid data-sentry-element="ModelsGrid" data-sentry-source-file="index.tsx">
                            {filteredArtStyles.map(style => <ArtStyleCard key={style.name} onClick={() => handleArtStyleClick(style)}>
                                    <Image src={style.url} alt={style.name} width={200} height={200} style={{
                objectFit: "cover",
                width: "100%",
                height: "auto"
              }} />
                                    <ArtStyleName>
                                        {style.name}
                                        {style.name.includes("*NEW*") && <NewBadge>✨ NEW ✨</NewBadge>}
                                    </ArtStyleName>
                                </ArtStyleCard>)}
                        </ModelsGrid>
                    </StyledScrollArea>
                </StyledDialogContent>
            </div>
        </Dialog>;
};
export default ArtStyleSelection;